import type { BriefingStage } from "@/types/briefing";
import { rowMatchesStage as sharedRowMatchesStage } from "@produce/shared";

interface StageLikeRow {
  stage_name: string;
  /**
   * Post-canonical migration: lineup + crew_assignments rows carry a
   * UUID FK to briefing_stages.id. Optional here because legacy rows
   * (or `assignment_type='custom'`) may still be NULL.
   */
  stage_id?: string | null;
}

// One small domain vocabulary so the rest of the code doesn't have to
// reason about the briefing.stages shape quirks:
//
//   - briefing.stages[i].name can be an internal per-day ID such as
//     "day0_stage1" (legacy scaffolding when the producer adds a stage
//     inside a day), or simply the label repeated.
//   - briefing.stages[i].label is the user-facing string.
//   - lineup.stage_name and crew_assignments.stage_name store the
//     user-facing label — text-cache, kept in sync by DB trigger.
//   - lineup.stage_id and crew_assignments.stage_id are UUID FKs to
//     briefing_stages.id (post 20260504240000_stage_id_canonical).
//
// Consumers should use stageKeyFor / matchesStage so any of those
// storage variants matches correctly. No more `stage.name ===
// lineup.stage_name` bugs.

type StageLike = Pick<BriefingStage, "name" | "label"> & {
  /**
   * Briefing_stages.id UUID — the stable FK target. Present on rows
   * loaded from the canonical briefing_stages table; absent on the
   * legacy briefings.stages JSONB shape.
   */
  id?: string;
};

/**
 * Canonical key for a stage — the value that both the display code and
 * the data filters agree on. Prefers the user-facing label; falls back
 * to name for briefings where only name was set.
 */
export function stageKeyFor(stage: StageLike): string {
  return stage.label || stage.name || "";
}

/**
 * True when a row carrying a stage_name column (lineup, assignments)
 * belongs to the given stage. Thin wrapper over the shared
 * `rowMatchesStage` helper — kept here for the orderedStagesForDay
 * caller's import surface.
 */
export function matchesStage(row: StageLikeRow, stage: StageLike): boolean {
  return sharedRowMatchesStage(row, stage);
}

/**
 * Pick the stages that should appear in the schedule timeline for the
 * selected day, in the producer's intended order.
 *
 * The ordered source of truth is briefings.stages (a JSON array stored
 * in the order the producer added the stages). We keep only stages that
 * have at least one artist in the day's lineup so empty stages don't
 * clutter the view. Entries that resolve to the same stageKey are
 * deduped — multi-day productions sometimes store one briefing.stages
 * entry per day with the same label, which previously rendered the
 * same stage N times.
 *
 * Legacy fallback: if briefingStages is empty (e.g. older briefings
 * where the array wasn't persisted), fall back to deriving stages from
 * the lineup insertion order.
 */
export function orderedStagesForDay(
  briefingStages: BriefingStage[] | null,
  lineupForDay: StageLikeRow[],
): BriefingStage[] {
  if (briefingStages && briefingStages.length > 0) {
    const seen = new Set<string>();
    const result: BriefingStage[] = [];
    for (const s of briefingStages) {
      const key = stageKeyFor(s);
      if (!key) continue;
      const hasArtist = lineupForDay.some((row) => matchesStage(row, s));
      if (!hasArtist) continue;
      if (seen.has(key)) continue;
      seen.add(key);
      result.push(s);
    }
    return result;
  }

  const seen = new Set<string>();
  const out: BriefingStage[] = [];
  for (const artist of lineupForDay) {
    if (!seen.has(artist.stage_name)) {
      seen.add(artist.stage_name);
      out.push({ name: artist.stage_name, label: artist.stage_name });
    }
  }
  return out;
}
